import { useEffect, useMemo, useRef, useState } from "react";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import Tooltip from "@components/dashboard/tooltip";
import {
  TREND_METHOD_COPY,
  TREND_ADJUSTMENT_COPY,
  STOCK_SOURCE_COPY,
  STOCK_IN_ORDER_PRESET,
  STOCK_IN_ORDER_HELP,
} from "./trendCopy";

const METHOD_KEYS = Object.keys(TREND_METHOD_COPY);
const STOCK_KEYS = Object.keys(STOCK_SOURCE_COPY);

const brl = (value) =>
  (value ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

let bandCounter = 0;
const withIds = (bands) => bands.map((band) => ({ ...band, id: `band-${(bandCounter += 1)}` }));
const withoutIds = (bands) => bands.map(({ from, to, method }) => ({ from, to, method }));

const validate = (bands) => {
  if (!bands.length || bands.length > 4) return "Informe de 1 a 4 faixas.";
  const sorted = [...bands].sort((a, b) => a.from - b.from);
  if (sorted[0].from !== 0) return "A primeira faixa deve começar em 0%.";
  if (sorted[sorted.length - 1].to !== 100) return "A última faixa deve terminar em 100%.";
  for (const band of sorted) {
    if (!(band.from < band.to)) return `Faixa inválida: ${band.from}% a ${band.to}%.`;
  }
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i].from !== sorted[i - 1].to) {
      return `Há lacuna ou sobreposição entre ${sorted[i - 1].to}% e ${sorted[i].from}%.`;
    }
  }
  return null;
};

// Configuração de Tendência e Estoque da construtora (não do usuário) — quantas faixas
// de avanço existem, qual fórmula cada uma usa, e de onde vem o estoque descontado do
// custo acumulado. Componente separado de Settings.jsx de propósito: aquela tela grava
// `user.configurations` (por usuário); misturar os dois faria o botão "Salvar" geral
// gravar config de empresa no documento do usuário. Mesmo padrão de
// ProcurementCyclesSection.
const TrendSettingsSection = ({ companies = [], defaultCompany }) => {
  const { getTrendSettings, updateTrendSettings, simulateTrend } = useProjectActions();
  const { activeProject, projects } = useProjectContext();

  // ⚠️ A empresa é escolhida EXPLICITAMENTE, nunca adivinhada. A obra ativa é só o palpite
  // inicial, e mesmo ela falha: medido no stg, o `company_id` da obra ativa não está entre as
  // empresas que a conta recebe. Cair na primeira da lista faria o usuário editar a
  // configuração de uma construtora achando que está editando outra — e salvar aqui move o
  // número financeiro de todas as obras dela.
  // Conta de UMA empresa (o caso do cliente) não tem escolha a fazer: seleciona sozinha e a
  // construtora aparece como texto, não como dropdown de uma opção. Conta multi-empresa (a
  // interna da Ampli) exige escolha explícita, porque salvar move o número da construtora
  // escolhida e errar de alvo é silencioso.
  const [companyId, setCompanyId] = useState(() => {
    if (companies.length === 1) return companies[0]._id;
    return companies.some((c) => c._id === defaultCompany?._id) ? defaultCompany._id : "";
  });
  const singleCompany = companies.length === 1;
  const company = companies.find((c) => c._id === companyId);
  const companyLabel = (c) => c?.name ?? c?.fantasy_name ?? c?._id;

  // ⚠️ A prévia roda sobre uma obra DA CONSTRUTORA ESCOLHIDA, não sobre a obra ativa global.
  // Antes usava `activeProject` direto, e com isso escolher "Thozen" calculava sobre a
  // ELIZABETH II — obra que não é da Thozen. O número saía aritmeticamente certo e não
  // correspondia a nenhuma obra da construtora em edição, que é a pior espécie de número.
  const companyProjects = (projects ?? []).filter((p) => p.company_id === companyId);
  const [previewProjectId, setPreviewProjectId] = useState("");
  useEffect(() => {
    if (!companyId) return setPreviewProjectId("");
    const mesmaEmpresa = companyProjects.some((p) => p._id === activeProject?._id);
    setPreviewProjectId(mesmaEmpresa ? activeProject._id : (companyProjects[0]?._id ?? ""));
  }, [companyId, projects, activeProject?._id]);

  const [bands, setBands] = useState([]);
  const [adjustments, setAdjustments] = useState({
    positive_availables: false,
    unposted_values: false,
    floor_committed: true,
  });
  const [stockSource, setStockSource] = useState("none");
  const [saved, setSaved] = useState(null);
  const [status, setStatus] = useState("");
  const [preview, setPreview] = useState(null);
  const previewTimer = useRef(null);

  useEffect(() => {
    if (!company?._id) return;
    let cancelled = false;
    getTrendSettings(company._id).then((res) => {
      if (cancelled || !res?.settings) return;
      setBands(withIds(res.settings.bands));
      setAdjustments(res.settings.adjustments);
      setStockSource(res.settings.stock_source);
      setSaved(res.settings);
    });
    return () => {
      cancelled = true;
    };
  }, [company?._id]);

  const error = useMemo(() => validate(bands), [bands]);

  const payload = useMemo(
    () => ({
      bands: withoutIds(bands),
      adjustments,
      stock_source: stockSource,
      zero_budget: saved?.zero_budget ?? "committed",
    }),
    [bands, adjustments, stockSource, saved]
  );

  // Prévia com os números da obra ESCOLHIDA para a prévia, com debounce para não bater no
  // endpoint a cada tecla. `project_id` é o `wbs_buildings._id` — o servidor deriva a empresa
  // e TODOS os códigos de EAP da obra a partir dele (obra multi-código soma todos os
  // `wbs_code_ids`, não só um).
  useEffect(() => {
    if (error || !company?._id || !previewProjectId) return;
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => {
      simulateTrend({
        project_id: previewProjectId,
        settings: payload,
      }).then((res) => setPreview(res?.ok ? res : null));
    }, 600);
    return () => clearTimeout(previewTimer.current);
  }, [payload, error, company?._id, previewProjectId]);

  const updateBand = (id, field, value) =>
    setBands((prev) => prev.map((b) => (b.id === id ? { ...b, [field]: value } : b)));

  const addBand = () =>
    setBands((prev) => {
      const last = prev[prev.length - 1];
      const split = Math.floor((last.from + last.to) / 2);
      return [
        ...prev.slice(0, -1),
        { ...last, to: split },
        ...withIds([{ from: split, to: 100, method: last.method }]),
      ];
    });

  const removeBand = (id) =>
    setBands((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((b) => b.id !== id);
      next[next.length - 1] = { ...next[next.length - 1], to: 100 };
      next[0] = { ...next[0], from: 0 };
      return next;
    });

  const applyPreset = () => {
    setBands(withIds(STOCK_IN_ORDER_PRESET.bands));
    setAdjustments(STOCK_IN_ORDER_PRESET.adjustments);
    // O preset só faz sentido com a fonte de estoque que ele pressupõe — sem isto, a
    // faixa única 0→100 de extrapolação ficaria calculando sobre um custo acumulado
    // sem o desconto do estoque comprado e não aplicado.
    setStockSource(STOCK_IN_ORDER_PRESET.stock_source);
  };

  const handleSave = async () => {
    if (error || !company?._id) return;
    const res = await updateTrendSettings({ company_id: company._id, ...payload });
    if (res?.ok) {
      setSaved(res.settings);
      setStatus("Salvo!");
    } else {
      setStatus(res?.errors?.join(" · ") || res?.message || "Erro ao salvar");
    }
    setTimeout(() => setStatus(""), 4000);
  };

  // Sem `return null` antecipado aqui: sem empresa escolhida ainda é preciso renderizar o
  // SELETOR, senão a seção volta a abrir vazia — que é exatamente o defeito que ela corrige.
  return (
    <div className="trend-settings">
      <p className="helper">
        A Tendência é a projeção de quanto a obra vai custar no fim. Ela é calculada{" "}
        <strong>item por item</strong> e somada: cada item usa a fórmula da faixa em que o
        avanço dele se encontra. Estas configurações valem para <strong>toda a construtora</strong>.
      </p>

      {/* Qual construtora está sendo editada não pode ficar implícito nem ser adivinhado. */}
      <div className="user-card">
        <label htmlFor="trend-company">Construtora</label>
        {singleCompany ? (
          <p><strong>{companyLabel(companies[0])}</strong></p>
        ) : (
          <select
            id="trend-company"
            className="default-input"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
          >
            <option value="">Selecione a construtora…</option>
            {companies.map((c) => (
              <option key={c._id} value={c._id}>{companyLabel(c)}</option>
            ))}
          </select>
        )}
        {!companyId && (
          <p className="helper">
            Escolha a construtora para carregar a configuração dela. Estas faixas valem para
            <strong> todas as obras</strong> da construtora escolhida.
          </p>
        )}
        {companyId && !activeProject?._id && (
          <p className="helper">
            Selecione uma obra no Dashboard para ver a prévia com os números reais.
          </p>
        )}
      </div>

      {!companyId ? null : (
      <>

      <div className="user-card">
        <h3>Faixas de avanço</h3>
        <p className="helper margin-left-8">
          Da esquerda para a direita: o limite de baixo entra na faixa, o de cima já pertence à
          faixa seguinte. A última faixa vale para tudo acima dela.
        </p>

        {bands.map((band) => (
          <div key={band.id} className="grid columns-4-tablet gap-8 margin-left-16 margin-top-8">
            <div>
              <label>De (%)</label>
              <input
                type="number"
                min={0}
                max={100}
                className="default-input"
                value={band.from}
                onChange={(e) => updateBand(band.id, "from", +e.target.value)}
              />
            </div>
            <div>
              <label>Até (%)</label>
              <input
                type="number"
                min={0}
                max={100}
                className="default-input"
                value={band.to}
                onChange={(e) => updateBand(band.id, "to", +e.target.value)}
              />
            </div>
            <div>
              <label>Cálculo</label>
              <select
                className="default-input"
                value={band.method}
                onChange={(e) => updateBand(band.id, "method", e.target.value)}
              >
                {METHOD_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {TREND_METHOD_COPY[key].label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex align-items-center">
              <button
                className="button button-secondary small"
                disabled={bands.length <= 1}
                onClick={() => removeBand(band.id)}
              >
                Remover
              </button>
            </div>
            <p className="helper" style={{ gridColumn: "1 / -1" }}>
              <strong>{TREND_METHOD_COPY[band.method].formula}</strong> —{" "}
              {TREND_METHOD_COPY[band.method].when} {TREND_METHOD_COPY[band.method].example}
            </p>
          </div>
        ))}

        <div className="flex gap-8 margin-left-16 margin-top-8">
          <button className="button button-secondary small" disabled={bands.length >= 4} onClick={addBand}>
            Adicionar faixa
          </button>
          <button className="button button-secondary small" onClick={applyPreset}>
            Usar preset "estoque em ordem"
          </button>
          <Tooltip title="Estoque em ordem" placement="top" message={STOCK_IN_ORDER_HELP} />
        </div>

        {error && <p className="helper" style={{ color: "tomato" }}>{error}</p>}
      </div>

      <div className="user-card">
        <h3>Ajustes</h3>
        {Object.keys(TREND_ADJUSTMENT_COPY).map((key) => (
          <div key={key} className="margin-left-16 margin-top-8">
            <input
              type="checkbox"
              checked={adjustments[key] === true}
              onChange={(e) => setAdjustments((prev) => ({ ...prev, [key]: e.target.checked }))}
            />
            <label>{TREND_ADJUSTMENT_COPY[key].label}</label>
            <p className="helper margin-left-8">{TREND_ADJUSTMENT_COPY[key].help}</p>
          </div>
        ))}
      </div>

      <div className="user-card">
        <h3>Estoque</h3>
        <p className="helper margin-left-8">
          Material comprado e ainda não aplicado já saiu do caixa, mas não virou obra. Descontá-lo do
          custo acumulado deixa a tendência mais fiel — desde que o estoque esteja atualizado.
        </p>
        <select
          className="default-input margin-left-16"
          value={stockSource}
          onChange={(e) => setStockSource(e.target.value)}
        >
          {STOCK_KEYS.map((key) => (
            <option key={key} value={key}>
              {STOCK_SOURCE_COPY[key].label}
            </option>
          ))}
        </select>
        <p className="helper margin-left-16">{STOCK_SOURCE_COPY[stockSource].help}</p>
      </div>

      <div className="user-card">
        <h3>Prévia</h3>
        {companyProjects.length === 0 ? (
          <p className="helper margin-left-8">
            {companyLabel(company)} não tem obra nesta conta, então não há números reais para
            simular. As faixas acima continuam podendo ser salvas.
          </p>
        ) : (
          <>
            <label htmlFor="trend-preview-project" className="margin-left-8">Obra da prévia</label>
            <select
              id="trend-preview-project"
              className="default-input"
              value={previewProjectId}
              onChange={(e) => setPreviewProjectId(e.target.value)}
            >
              {companyProjects.map((p) => (
                <option key={p._id} value={p._id}>
                  {(p.building_name || p.description || p._id).trim()}
                </option>
              ))}
            </select>
            <p className="helper margin-left-8">
              Só obras de <strong>{companyLabel(company)}</strong> aparecem aqui — a prévia tem
              que representar a construtora que você está editando.
            </p>
            {!preview && <p className="helper margin-left-8">Calculando…</p>}
          </>
        )}
        {preview && (
          <>
            {preview.warnings?.length > 0 && (
              <div className="margin-left-8">
                {preview.warnings.map((warning) => (
                  <p key={warning} className="helper" style={{ color: "tomato" }}>
                    ⚠ {warning}
                  </p>
                ))}
              </div>
            )}
            <div className="grid columns-2-tablet gap-8 margin-left-16">
              <div>
                <label>Com a configuração em edição</label>
                <p className="bold">{brl(preview.total)}</p>
              </div>
              <div>
                <label>Itens considerados</label>
                <p>{preview.leaf_count}</p>
              </div>
            </div>
            {preview.boundaries?.length > 0 && (
              <>
                <p className="helper margin-left-16 margin-top-8">
                  O que acontece ao cruzar cada fronteira — se a diferença entre as duas linhas de um
                  par for grande, a tendência dá um salto naquele ponto:
                </p>
                <div className="grid columns-2-tablet gap-8 margin-left-16">
                  {preview.boundaries.map((boundary) => (
                    <div key={`${boundary.percent}-${boundary.label}`}>
                      <label>{boundary.label}</label>
                      <p>{brl(boundary.value)}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <button
        className={`button button-${status === "Salvo!" ? "green" : "accent"} margin-left-16`}
        disabled={!!error}
        onClick={handleSave}
      >
        {status || "Salvar configuração da tendência"}
      </button>
      </>
      )}
    </div>
  );
};

export default TrendSettingsSection;
